import {NULL_SENTINEL} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {AMARON_KANBAN_COLUMN_FIELD} from "~/constants/amaronDispositionMapping";
import {getAmaronDispositionApiColumnId, isAmaronJourneyDispositionUiFilterColumn} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import {amaronJourneyAonConfig} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {isAonFilterRangesLineItem, isAonUiOnlyFilterLineItem} from "~/utilities/syntheticFields/aon";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getOptionalEventContainerDispositionId} from "~/utilities/ottopilotSemanticColumns";

type AmaronDispositionKanbanFilterRow = Record<string, unknown> & {
    id: Uuid;
};

function toComparableTimestamp(value: unknown): number | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number" && Number.isFinite(value) === true) {
        return value;
    }
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) === true ? parsed : null;
}

function compareComparableValues(columnValue: unknown, filterValue: unknown, compare: (left: number, right: number) => boolean): boolean | null {
    const columnMs = toComparableTimestamp(columnValue);
    const filterMs = toComparableTimestamp(filterValue);
    if (columnMs === null || filterMs === null) {
        return null;
    }
    return compare(columnMs, filterMs);
}

function isEmptyColumnValue(columnValue: unknown): boolean {
    if (columnValue === null || columnValue === undefined) {
        return true;
    }
    return String(columnValue).trim() === "";
}

function passesAmaronDispositionKanbanFilterLineItem(row: AmaronDispositionKanbanFilterRow, filter: FilterConfigurationLineItem): boolean {
    const columnValue = row[filter.columnId];
    const isArrayColumn = Array.isArray(columnValue);

    switch (filter.operation) {
        case FilterOperation.contains: {
            if (isArrayColumn) {
                return (columnValue as Array<unknown>).some((value) => String(value).toLowerCase().includes(String(filter.value).toLowerCase()));
            }
            return String(columnValue ?? "")
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
        }
        case FilterOperation.in: {
            if (filter.values === undefined || filter.values.length === 0) {
                return false;
            }
            if (isEmptyColumnValue(columnValue) === true) {
                return filter.values.includes(NULL_SENTINEL);
            }
            if (isArrayColumn) {
                return (columnValue as Array<unknown>).some((value) => filter.values.includes(String(value)));
            }
            return filter.values.includes(String(columnValue));
        }
        case FilterOperation.equals: {
            if (filter.value === null) {
                return columnValue === null || columnValue === undefined;
            }
            return columnValue === filter.value;
        }
        case FilterOperation.notEquals: {
            if (filter.value === null) {
                return columnValue !== null && columnValue !== undefined;
            }
            return columnValue !== filter.value;
        }
        case FilterOperation.greaterThan: {
            return compareComparableValues(columnValue, filter.value, (left, right) => left > right) ?? (columnValue as number) > (filter.value as number);
        }
        case FilterOperation.greaterThanEqualTo: {
            return compareComparableValues(columnValue, filter.value, (left, right) => left >= right) ?? (columnValue as number) >= (filter.value as number);
        }
        case FilterOperation.lessThan: {
            return compareComparableValues(columnValue, filter.value, (left, right) => left < right) ?? (columnValue as number) < (filter.value as number);
        }
        case FilterOperation.lessThanEqualTo: {
            return compareComparableValues(columnValue, filter.value, (left, right) => left <= right) ?? (columnValue as number) <= (filter.value as number);
        }
        case FilterOperation.startsWith: {
            return String(columnValue ?? "")
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
        }
        case FilterOperation.endsWith: {
            return String(columnValue ?? "")
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
        }
        case FilterOperation.notContains: {
            if (isArrayColumn) {
                return (columnValue as Array<unknown>).every((value) => String(value).toLowerCase().includes(String(filter.value).toLowerCase()) === false);
            }
            return (
                String(columnValue ?? "")
                    .toLowerCase()
                    .includes(String(filter.value).toLowerCase()) === false
            );
        }
        default: {
            return true;
        }
    }
}

function buildAmaronDispositionKanbanFilterRow(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
): AmaronDispositionKanbanFilterRow {
    const dispositionId = getOptionalEventContainerDispositionId(journey, eventContainerTemplate);
    const row: AmaronDispositionKanbanFilterRow = {
        id: journey.id,
        createdAt: journey.createdAt,
        updatedAt: journey.updatedAt,
        createdBy: journey.createdBy,
        [getAmaronDispositionApiColumnId("journey")]: dispositionId === null ? "" : String(dispositionId),
    };

    for (const [columnId, value] of Object.entries(journey.additionalColumnValues)) {
        row[columnId] = value;
    }

    return row;
}

function shouldApplyClientFilterLineItem(lineItem: FilterConfigurationLineItem): boolean {
    if (lineItem.columnId === AMARON_KANBAN_COLUMN_FIELD) {
        return false;
    }
    if (isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true) {
        return false;
    }
    if (lineItem.columnId === getAmaronDispositionApiColumnId("journey")) {
        return false;
    }
    if (isAonFilterRangesLineItem(amaronJourneyAonConfig, lineItem) === true) {
        return false;
    }
    if (isAonUiOnlyFilterLineItem(amaronJourneyAonConfig, lineItem) === true) {
        return false;
    }
    return true;
}

export function hasAmaronDispositionKanbanEmptyInFilter(filterConfiguration: FilterConfiguration): boolean {
    return filterConfiguration.lineItems.some(
        (lineItem) =>
            shouldApplyClientFilterLineItem(lineItem) === true &&
            lineItem.operation === FilterOperation.in &&
            (lineItem.values === undefined || lineItem.values.length === 0),
    );
}

export function applyAmaronDispositionTrackerClientFilters(
    journeys: Array<EventContainerWithEvents>,
    filterConfiguration: FilterConfiguration,
    eventContainerTemplate: EventContainerTemplate,
): Array<EventContainerWithEvents> {
    if (hasAmaronDispositionKanbanEmptyInFilter(filterConfiguration) === true) {
        return [];
    }

    const clientLineItems = filterConfiguration.lineItems.filter((lineItem) => shouldApplyClientFilterLineItem(lineItem));
    if (clientLineItems.length === 0) {
        return journeys;
    }

    const filterRows = journeys.map((journey) => buildAmaronDispositionKanbanFilterRow(journey, eventContainerTemplate));
    const matchingIds = new Set<Uuid>();

    for (const row of filterRows) {
        let matchesAll = true;
        for (const lineItem of clientLineItems) {
            if (passesAmaronDispositionKanbanFilterLineItem(row, lineItem) !== true) {
                matchesAll = false;
                break;
            }
        }
        if (matchesAll === true) {
            matchingIds.add(row.id);
        }
    }

    return journeys.filter((journey) => matchingIds.has(journey.id));
}
